import { SelectProps } from "@cofano/cck/Select/Select";
import { LaytimeCountingEvent } from "__generated__/graphql";
import { Select } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import client from "../../../../apollo";
import { formatDate } from "utils/DateUtils";
import { DataFormat } from "utils";
import { useStoreState } from "ezStore";
import { LaytimeCountingOption } from "view/charters/types/demurrage";
import {
	GetStatusChangesWithEntityDocument,
} from "__generated__/view/statuses/graphql/queries.generated";
import { SortDirection } from "__generated__/enums";

type LaytimeCountingSearch = {
	startLaytime?: boolean;
	endLaytime?: boolean;
	callId: number;
};

type LaytimeCountingSelectProps = Omit<SelectProps<LaytimeCountingOption, false>, "fetchOptions" | "paginated"> & {
	search: LaytimeCountingSearch;
	additionalEvents?: LaytimeCountingEvent[];
};

const LaytimeCountingSelect = ({
	search,
	additionalEvents = [],
	...rest
}: LaytimeCountingSelectProps) => {
	const session = useStoreState(store => store.session);

	const onFetchOptionsPaginated = async(searchTerm: string, page: number) => {
		// Static event options - only show on first page, exclude "Status" since users will select actual statuses
		const events: LaytimeCountingEvent[] = [
			...new Set([
				LaytimeCountingEvent.Custom,
				...additionalEvents,
			]),
		];

		const eventOptions: LaytimeCountingOption[] = page === 1 ? events.map(event => ({
			type: "event" as const,
			event,
		})) : [];

		// Fetch statuses
		try {
			const result = await client.query({
				query: GetStatusChangesWithEntityDocument,
				variables: {
					pagination: {
						page: page - 1,
						size: 5,
						sortList: [ { field: "status.statusOrder", direction: SortDirection.Desc } ],
					},
					search: {
						hasActualTimeFilledIn: true,
						cargosOnCallIds: [ search.callId ],
						statusSearch: {
							laytimeStartStatus: search.startLaytime,
							laytimeEndStatus: search.endLaytime,
						},
					},
				},
			});

			const statusOptions: LaytimeCountingOption[] = result.data.statusChanges.content
				.sort((a, b) => (a.status.statusOrder ?? 0) - (b.status.statusOrder ?? 0))
				.map(statusChange => ({
					type: "status" as const,
					statusChange: statusChange as never,
				}));

			return {
				options: [ ...eventOptions, ...statusOptions ],
				totalResults: eventOptions.length + (result.data.statusChanges.totalElements || 0),
			};
		} catch (error) {
			return {
				options: eventOptions,
				totalResults: eventOptions.length,
			};
		}
	};

	const getOptionLabel = (option: LaytimeCountingOption) => {
		if (option.type === "event") {
			return intl.formatMessage({ id: `LAYTIME_COUNTING_EVENT_${option.event}` });
		} else {
			const statusLabel = DataFormat.status(option.statusChange.status, session.locale);
			const dateLabel = formatDate(option.statusChange.actualTime);
			const cargoNumber = option.statusChange.cargo.cargoNumber;

			if (cargoNumber) {
				return `${statusLabel} - ${intl.formatMessage({ id: "CARGO" })} ${cargoNumber}`;
			}

			return `${statusLabel} - ${dateLabel}`;
		}
	};

	const getOptionColor = (option: LaytimeCountingOption) => {
		if (option.type === "status") {
			return option.statusChange.status.color;
		}
		return "#ddd";
	};

	return (
		<Select
			fetchOptions={onFetchOptionsPaginated}
			getOptionColor={getOptionColor}
			getOptionLabel={getOptionLabel}
			popoverHeight={350}
			paginated
			{...rest}
		/>
	);
};

export default LaytimeCountingSelect;
